import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withRouter } from "react-router-dom";
import posthog from "posthog-js";
import { Popup, List, Icon, Button, Dropdown } from "semantic-ui-react";
import Badge from "./Badge/Badge";
import UnreadCountBadge from "./UnreadCountBadge";
import { Link } from "react-router-dom";
import moment from "moment";
import * as notificationActions from "../domain/notifications/notificationActions";
import { getUserInfo } from "../../api/axios";
import { isSubscriptionSuspended, fetchSubscriptionData } from "../../utils/subscriptionUtils";
import "./GeneralNotification.css";

class GeneralNotification extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showPopup: false,
            isSubscriptionSuspended: false,
        };
    }

    async componentDidMount() {
        // Check subscription status
        try {
            const subscriptionData = await fetchSubscriptionData();
            if (subscriptionData && isSubscriptionSuspended(subscriptionData)) {
                this.setState({ isSubscriptionSuspended: true });
            }
        } catch (error) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("generalNotificationSubscription", {
                    type: "exception",
                    success: false,
                    reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
                    status: error?.response?.status,
                });
            }
        }
        // Don't load notifications on mount, only when bell is clicked
    }

    componentDidUpdate(prevProps) {
        // Refresh notifications when user changes
        const currentUser = getUserInfo();
        const prevUser = prevProps.currentUser;

        if (currentUser?.id !== prevUser?.id) {
            this.loadNotifications();
        }
    }

    loadNotifications = () => {
        const { notifications } = this.props.notificationReducer;

        // If we already have notifications loaded (e.g., from NotificationsScreen),
        // don't replace them with just 10. Only load if we have fewer than 10 or none.
        if (notifications.length >= 10) {
            // Use existing notifications, don't make API call
            console.log('Using existing notifications, skipping API call');
            return;
        }

        // Only load if we have fewer than 10 notifications
        // Use a flag to indicate this is a preview load, not a full refresh
        this.props.notificationActions.getUserNotifications(0, 1, 10, true); // true = isPreview
    };

    togglePopup = () => {
        if (!this.state.showPopup) {
            // Load notifications when opening the popup
            this.loadNotifications();
        }
        this.setState({ showPopup: !this.state.showPopup });
    };

    handleRefresh = () => {
        this.loadNotifications();
    };

    handleViewAll = () => {
        // Mark all notifications as seen when user clicks "View All"
        this.props.notificationActions.markAllNotificationsAsSeen();
        this.setState({ showPopup: false });
        // Navigate to notifications page using withRouter
        this.props.history.push('/notifications');
    };

    handlePopupClose = () => {
        // Mark all notifications as seen when popup is closed
        this.props.notificationActions.markAllNotificationsAsSeen();
        this.setState({ showPopup: false });
    };

    handleNotificationClick = (notification) => {
        // Handle notification action if needed
        if (notification.actionUrl) {
            this.props.history.push(notification.actionUrl);
        }

        this.setState({ showPopup: false });
    };

    render() {
        const { notificationReducer } = this.props;
        const { notifications, unreadCount, loading } = notificationReducer;
        const { showPopup, isSubscriptionSuspended } = this.state;

        // Hide notification bell if subscription is suspended
        if (isSubscriptionSuspended) {
            return null;
        }

        // Show first 10 notifications for the popup (preview)
        // If we have more than 10, just show the first 10 without making API call
        const recentNotifications = notifications.slice(0, 10);

        const trigger = (
            <div className="general-notification-trigger" onClick={this.togglePopup}>
                <Icon name="bell outline" size="large" />
                <UnreadCountBadge />
            </div>
        );

        const content = (
            <div className="general-notification-popup">
                <div className="popup-header">
                    <div className="popup-header-left">
                        <div className="popup-header-text">
                            <h4>
                                Notifications
                                {unreadCount > 0 && (
                                    <span className="popup-unread-count">{unreadCount}</span>
                                )}
                            </h4>
                        </div>
                    </div>
                    <div className="header-actions">
                        <Button
                            size="mini"
                            loading={loading}
                            onClick={this.handleRefresh}
                            className="refresh-button"
                            basic
                        >
                            <Icon name="refresh" />
                        </Button>
                        <Button
                            size="mini"
                            onClick={this.handleViewAll}
                            className="view-all-button"
                        >
                            View All
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">
                        <Icon name="refresh" loading size="large" />
                        <p>Loading notifications...</p>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="empty-state">
                        <Icon name="bell outline" color="grey" size="large" />
                        <p>No notifications yet.</p>
                    </div>
                ) : (
                    <>
                        <List relaxed className="notification-list" style={{ marginTop: '3px', marginBottom: '1px' }}>
                            {recentNotifications.map((notification) => (
                                <List.Item
                                    key={notification.id}
                                    className={`notification-item ${!notification.isSeen ? 'unread' : ''}`}
                                    onClick={() => this.handleNotificationClick(notification)}
                                >
                                    <List.Content>
                                        <div className="notification-content">
                                            {!notification.isSeen && (
                                                <div className="unread-indicator"></div>
                                            )}
                                            <div className="notification-body">
                                                <div className="notification-message">
                                                    {notification.message}
                                                </div>
                                                {notification.type && (
                                                    <div className="notification-type">
                                                        <span className="type-badge">{notification.type.replace('_', ' ')}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="notification-meta">
                                                <div className="notification-time">
                                                    {moment(notification.notificationDate).format("MMM DD, HH:mm")}
                                                </div>
                                            </div>
                                        </div>
                                    </List.Content>
                                </List.Item>
                            ))}
                        </List>

                        {notifications.length >= 10 && (
                            <div className="more-notifications">
                                <Button
                                    fluid
                                    onClick={this.handleViewAll}
                                    className="view-all-notifications-btn"
                                >
                                    View all notifications
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </div>
        );

        return (
            <Popup
                trigger={trigger}
                content={content}
                on="click"
                position="bottom right"
                open={showPopup}
                onOpen={() => this.setState({ showPopup: true })}
                onClose={this.handlePopupClose}
                className="general-notification"
                size="large"
            />
        );
    }

    getNotificationIcon = (type) => {
        const iconMap = {
            'leave_request': 'calendar',
            'approval': 'check circle',
            'system': 'cog',
            'reminder': 'clock',
            'update': 'info circle',
            'alert': 'exclamation triangle'
        };
        return iconMap[type] || 'bell';
    };
}

const mapStateToProps = (state) => ({
    notificationReducer: state.notificationReducer,
    currentUser: state.accountReducer?.userInfo,
});

const mapDispatchToProps = (dispatch) => ({
    notificationActions: bindActionCreators(notificationActions, dispatch),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(GeneralNotification));
